/**
 * HeaderListingPage - Reusable header component for listing pages
 * Based on Activities.tsx and WorkPlans.tsx listing page headers
 */

// react
import { ReactNode } from 'react';

// mui
import { alpha, Box, Paper, Stack, Typography } from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';

// design system
import { radius, shadows, spacing, typography } from '../../../utils/designSystem';

// data
import { GRADIENTS } from '../../../data/colors';

// ui
import { TruncatedText } from '../TruncatedText/TruncatedText';

// Types for color variants
type ColorVariant =
	| 'activity'
	| 'workPlan'
	| 'contract'
	| 'condition'
	| 'financial'
	| 'payment'
	| 'document';

interface StatItem {
	/** The stat label */
	label: string;
	/** The stat value (can be number or formatted string) */
	value: string | number;
	/** Optional secondary value (e.g., currency amount) */
	subValue?: string;
	/** Optional color for the value */
	color?: string;
}

interface HeaderListingPageProps {
	/** The icon component to display */
	icon: SvgIconComponent;
	/** The main title */
	title: string;
	/** Optional subtitle/description */
	subtitle?: string;
	/** Color variant for the header styling */
	variant?: ColorVariant;
	/** Optional chip/badge to display next to the title */
	titleBadge?: ReactNode;
	/** Optional secondary badge (e.g. custom fields link) shown after titleBadge */
	secondaryBadge?: ReactNode;
	/** Action buttons (right side) */
	actions?: ReactNode;
	/** Optional stats to display below the header */
	stats?: StatItem[];
}

export const HeaderListingPage = ({
	icon: Icon,
	title,
	subtitle,
	variant = 'activity',
	titleBadge,
	secondaryBadge,
	actions,
	stats,
}: HeaderListingPageProps) => {

	// Get colors based on variant
	const colors = GRADIENTS[variant] || GRADIENTS.activity;

	return (
		<Paper
			elevation={0}
			sx={{
				bgcolor: alpha(colors.border, 0.04),
				borderBottom: '1px solid',
				borderColor: 'divider',
				borderRadius: 0,
				mb: 0,
				px: { xs: spacing.sm, sm: spacing.md },
				py: spacing.md,
			}}>
			<Stack direction="row" justifyContent="space-between" alignItems="center">
				<Stack direction="row" alignItems="center" spacing={{ xs: spacing.sm, md: spacing.md }}>
					{/* Gradient Icon Box */}
					<Box
						sx={{
							alignItems: 'center',
							background: `linear-gradient(135deg, ${colors.icon}, ${colors.border})`,
							borderRadius: radius.md,
							boxShadow: shadows.icon(colors.icon),
							display: 'flex',
							flexShrink: 0,
							height: { xs: 44, sm: 52 },
							justifyContent: 'center',
							width: { xs: 44, sm: 52 },
						}}>
						<Icon sx={{ color: '#fff', fontSize: { xs: 22, sm: 26 } }} />
					</Box>

					{/* Title & Subtitle */}
					<Box>
						<Stack direction="row" alignItems="center" spacing={1.5}>
							<Typography
								sx={{
									...typography.h2,
									color: colors.text,
									fontSize: { xs: '1.375rem', sm: '1.5rem', md: '1.75rem' },
								}}>
								{title}
							</Typography>
							{titleBadge}
						</Stack>
						{subtitle && (
							<Typography
								sx={{
									...typography.body,
									color: 'text.secondary',
									fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
								}}>
								{subtitle}
							</Typography>
						)}
					</Box>
				</Stack>

				{/* Actions */}
				<Stack direction="row" alignItems="center" spacing={2}>
					{secondaryBadge}
					{actions}
				</Stack>
			</Stack>

			{/* Stats Row */}
			{stats && stats.length > 0 && (
				<Stack
					direction="row"
					spacing={4}
					sx={{
						mt: spacing.md,
						pt: spacing.md,
						borderTop: '1px solid',
						borderColor: alpha(colors.icon, 0.15),
					}}>
					{stats.map((stat, index) => (
						<Box key={index} sx={{ minWidth: 0, maxWidth: 240 }}>
							<TruncatedText
								text={String(stat.value)}
								variant="h6"
								sx={{ fontWeight: 700, color: stat.color || colors.icon }}
							/>
							<Typography variant="body2" color="text.secondary">
								{stat.label}
							</Typography>
							{stat.subValue && (
								<TruncatedText
									text={stat.subValue}
									variant="body2"
									sx={{ display: 'block', color: stat.color || colors.text, fontWeight: 600 }}
								/>
							)}
						</Box>
					))}
				</Stack>
			)}
		</Paper>
	);
};
